import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, Check, CircleSlash, Clock } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useApiClient } from "../../../api/ApiClientContext.js";
import { ApiClientError } from "../../../api/errors.js";
import type { DealTransferLesson, Instructor } from "../../../api/schemas.js";
import { createIdempotencyKey } from "../../../lib/idempotency.js";
import { formatInTimeZone } from "../../../lib/timezone.js";
import { DialogShell } from "../../../ui/DialogShell.js";
import type { AccessibleError } from "../model/types.js";

export interface DealTransferDialogProps {
  readonly open: boolean;
  readonly leadId: number;
  readonly dealName?: string | null;
  /** Кандидаты — инструкторы того же острова: календарь уже сузил список до своей локации. */
  readonly instructors: readonly Instructor[];
  /** Запасной пояс — для урока, у которого площадки ещё нет и своего пояса взять неоткуда. */
  readonly fallbackTimeZone: string;
  readonly onClose: () => void;
  readonly onTransferred: () => void;
  readonly onError: (error: AccessibleError) => void;
}

const VERDICT_TEXT: Record<DealTransferLesson["verdict"], string> = {
  held: "проведён — остаётся за прежним",
  unscheduled: "без времени",
  free: "свободен",
  busy: "занят в это время",
};

function verdictIcon(verdict: DealTransferLesson["verdict"]) {
  if (verdict === "held") return CircleSlash;
  if (verdict === "busy") return AlertTriangle;
  if (verdict === "unscheduled") return Clock;
  return Check;
}

/**
 * Форс-мажор: инструктор выпал, сделку ведёт другой.
 *
 * Передаётся сделка целиком — учебный план у неё зафиксирован, и делить его между людьми
 * значило бы делить одного клиента. Диалог сначала показывает, что именно произойдёт с каждым
 * уроком, и только потом даёт нажать: «Мария свободна в четверг, но занята в пятницу» надо
 * увидеть до подтверждения, а не узнать из отказа на середине пачки.
 *
 * Уже проведённые уроки остаются за тем, кто их провёл, — вместе с деньгами, которые по ним
 * приняли: один форс-мажор не должен переписывать заработок задним числом.
 */
export function DealTransferDialog(props: DealTransferDialogProps) {
  const api = useApiClient();
  const qc = useQueryClient();
  const [toInstructorId, setToInstructorId] = useState("");
  const [keepConflictingTimes, setKeepConflictingTimes] = useState(false);
  const [reason, setReason] = useState("");

  const planQuery = useQuery({
    queryKey: ["deal-transfer-preview", props.leadId, toInstructorId, keepConflictingTimes],
    queryFn: () =>
      api.previewDealInstructorTransfer(props.leadId, toInstructorId, keepConflictingTimes),
    enabled: props.open && toInstructorId !== "",
    retry: false,
  });

  const transferMutation = useMutation({
    mutationFn: () =>
      api.transferDealInstructor(props.leadId, {
        toInstructorId,
        keepConflictingTimes,
        reason: reason.trim(),
        idempotencyKey: createIdempotencyKey("deal-transfer"),
      }),
    onSuccess: async () => {
      // Календарь, учебный план и список зачислений — всё показывает инструктора.
      await qc.invalidateQueries({ queryKey: ["calendar"] });
      await qc.invalidateQueries({ queryKey: ["deal-lessons", props.leadId] });
      await qc.invalidateQueries({ queryKey: ["deal-enrollments", props.leadId] });
      await qc.invalidateQueries({ queryKey: ["admin", "enrollments"] });
      props.onTransferred();
      props.onClose();
    },
    onError: (error: unknown) => {
      const correlationId = error instanceof ApiClientError ? error.correlationId : undefined;
      props.onError({
        id: `deal-transfer-${correlationId || Date.now()}`,
        message: error instanceof Error ? error.message : "Не удалось передать сделку",
        code: error instanceof ApiClientError ? error.code : "client_error",
        ...(correlationId ? { correlationId } : {}),
      });
    },
  });

  const plan = planQuery.data ?? null;
  const planError = planQuery.error;
  const ready = toInstructorId !== "" && plan !== null && reason.trim().length > 0;

  return (
    <DialogShell
      open={props.open}
      title="Передать сделку другому инструктору"
      onClose={props.onClose}
      testId="deal-transfer-dialog"
      footer={
        <>
          <Button type="button" variant="outline" onClick={props.onClose}>
            Отмена
          </Button>
          <Button
            type="button"
            data-testid="deal-transfer-submit"
            disabled={!ready || transferMutation.isPending}
            onClick={() => transferMutation.mutate()}
          >
            {transferMutation.isPending
              ? "Передаём…"
              : plan
                ? `Передать ${plan.movingCount} ${lessonWord(plan.movingCount)}`
                : "Передать сделку"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="max-w-prose text-ink-600 text-sm">
          {props.dealName ? `${props.dealName} · ` : ""}
          Все курсы сделки и все непроведённые уроки уйдут новому инструктору. Проведённые останутся
          за тем, кто их провёл, — вместе с деньгами, которые по ним приняли.
        </p>

        <div>
          <Label htmlFor="deal-transfer-instructor">Новый инструктор</Label>
          <Select value={toInstructorId} onValueChange={setToInstructorId}>
            <SelectTrigger
              id="deal-transfer-instructor"
              data-testid="deal-transfer-instructor"
              className="mt-1 h-10 w-full"
            >
              <SelectValue placeholder="Выберите инструктора" />
            </SelectTrigger>
            <SelectContent>
              {props.instructors.map((instructor) => (
                <SelectItem key={instructor.id} value={instructor.id}>
                  {instructor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {planError ? (
          <p
            className="text-danger-500 text-sm"
            role="alert"
            data-testid="deal-transfer-plan-error"
          >
            {planError instanceof Error ? planError.message : "Не удалось собрать план передачи"}
          </p>
        ) : null}

        {plan ? (
          <>
            <p className="text-ink-600 text-sm" data-testid="deal-transfer-summary">
              {plan.fromInstructors.map((from) => from.name ?? from.id).join(", ") || "—"}
              <ArrowRight className="mx-1.5 inline size-3.5" aria-hidden="true" />
              {plan.toInstructorName ?? plan.toInstructorId}
            </p>

            <ul className="divide-y divide-pad-200" data-testid="deal-transfer-lessons">
              {plan.lessons.map((lesson) => {
                const Icon = verdictIcon(lesson.verdict);
                return (
                  <li
                    key={lesson.lessonId}
                    className="flex items-start gap-2 py-2 text-sm"
                    data-testid={`deal-transfer-lesson-${lesson.lessonId}`}
                  >
                    <Icon
                      className={`mt-0.5 size-4 shrink-0 ${
                        lesson.verdict === "busy"
                          ? "text-danger-500"
                          : lesson.verdict === "held"
                            ? "text-ink-400"
                            : "text-ok-500"
                      }`}
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className="text-ink-900">
                        Урок {lesson.lessonOrder}
                        {lesson.startTime
                          ? ` · ${formatInTimeZone(
                              new Date(lesson.startTime),
                              lesson.venueTimezone ?? props.fallbackTimeZone,
                            )}`
                          : ""}
                        {lesson.venueName ? ` · ${lesson.venueName}` : ""}
                      </span>
                      <span className="block text-ink-600 text-xs">
                        {VERDICT_TEXT[lesson.verdict]}
                        {lesson.verdict === "busy" && !lesson.keepsTime
                          ? " → время будет снято"
                          : ""}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>

            {plan.conflictCount > 0 ? (
              <label className="flex items-start gap-2 text-ink-700 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  data-testid="deal-transfer-keep-times"
                  checked={keepConflictingTimes}
                  onChange={(event) => setKeepConflictingTimes(event.target.checked)}
                />
                <span className="min-w-0">
                  Оставить время там, где новый инструктор занят.
                  <span className="block text-ink-600 text-xs">
                    Два ученика на одной площадке под присмотром одного инструктора бывают. Без
                    галочки такие уроки вернутся в «без времени», и время назначают заново.
                  </span>
                </span>
              </label>
            ) : null}
          </>
        ) : null}

        <div>
          <Label htmlFor="deal-transfer-reason">Причина</Label>
          <Input
            id="deal-transfer-reason"
            data-testid="deal-transfer-reason"
            className="mt-1 h-10"
            placeholder="Например: инструктор заболел"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <p className="mt-1 text-ink-600 text-xs">
            Останется в журнале изменений: через месяц «почему сделка у другого» — первый вопрос.
          </p>
        </div>
      </div>
    </DialogShell>
  );
}

function lessonWord(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return "урок";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "урока";
  return "уроков";
}
